/**
 * §14.8 screen 10 — Rasmiy hisobotlar.
 *
 * Two outputs and no new infrastructure: `?format=csv` on the stats endpoints,
 * and a PRINT VIEW that is the browser's own print of this page. No PDF
 * service, no report renderer, no template engine — a hokimiyat needs a sheet
 * of paper with defensible numbers on it, and `window.print()` produces one.
 *
 * ===========================================================================
 * WHAT THIS SCREEN EXPORTS, AND WHAT IT REFUSES TO.
 * ===========================================================================
 *
 * §14.10.8: **aggregates only.** Every column below is a count, a ratio, a date
 * or a mahalla name from our own public registry. There is no citizen-level
 * export — a file of names, phones and appeal bodies is a PII exfiltration
 * channel with no per-row audit, and if one is ever needed it is a separate,
 * `admin`-only, audit-logged endpoint with a named legal basis. It is not this
 * screen and it is not a checkbox on this screen.
 *
 * The printed sheet carries the SAME honesty rules as the interactive screens:
 * the four legal-type buckets with `unclassified` present, the two SLA numbers
 * separately labelled, and an em dash wherever a ratio has no denominator.
 */
import { useCallback, useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { LEGAL_TYPE_BUCKETS } from '@navoiy/domain';
import { ApiError, api, apiBaseUrl, errorKey, jsonBody, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';
import {
  formatDate,
  formatDateTime,
  formatMetric,
  formatPercent,
  fromDateInput,
} from '../lib/format.js';
import { useSummary } from '../lib/useSummary.js';
import {
  Button,
  Card,
  ErrorBanner,
  Field,
  Spinner,
  Table,
  Td,
  Th,
  TextInput,
  Tr,
} from '../ui/index.jsx';

type Breakdown = components['schemas']['AdminBreakdownResponse'];
type MahallaStats = components['schemas']['AdminMahallaStatsResponse'];

interface Bounds {
  from: string;
  to: string;
}

const EMPTY_BOUNDS: Bounds = { from: '', to: '' };

/** The three aggregate exports §14.10.8 permits. */
const EXPORTS = [
  { name: 'mahallas', labelKey: 'admin.reports.exportMahallas' },
  { name: 'breakdown', labelKey: 'admin.reports.exportBreakdown' },
  { name: 'timeseries', labelKey: 'admin.reports.exportTimeseries' },
] as const;

export function ReportsScreen(): React.JSX.Element {
  const { t, locale } = useI18n();
  const { expire, allows } = useSession();
  const { summary, serverNow } = useSummary();

  const [bounds, setBounds] = useState<Bounds>(EMPTY_BOUNDS);
  const [applied, setApplied] = useState<Bounds>(EMPTY_BOUNDS);
  const [breakdown, setBreakdown] = useState<Breakdown | null>(null);
  const [mahallas, setMahallas] = useState<MahallaStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = fromDateInput(applied.from);
      const to = fromDateInput(applied.to, true);
      const range = { ...(from ? { from } : {}), ...(to ? { to } : {}) };
      const [breakdownResult, mahallaResult] = await Promise.all([
        api().GET('/admin/stats/breakdown', { params: { query: range } }),
        api().GET('/admin/stats/mahallas', { params: { query: { ...range, locale } as never } }),
      ]);
      setBreakdown(jsonBody(unwrap(breakdownResult)));
      setMahallas(jsonBody(unwrap(mahallaResult)));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        expire();
        return;
      }
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    } finally {
      setLoading(false);
    }
  }, [applied, locale, expire, t]);

  useEffect(() => {
    void load();
  }, [load]);

  function csvHref(name: string): string {
    const from = fromDateInput(applied.from);
    const to = fromDateInput(applied.to, true);
    const query = [
      'format=csv',
      ...(from ? [`from=${encodeURIComponent(from)}`] : []),
      ...(to ? [`to=${encodeURIComponent(to)}`] : []),
      ...(name === 'timeseries' ? ['bucket=month'] : []),
      ...(name === 'mahallas' ? [`locale=${locale}`] : []),
    ].join('&');
    return `${apiBaseUrl()}/admin/stats/${name}?${query}`;
  }

  const legal = breakdown?.by_legal_type;
  const kpi = summary?.kpi;

  return (
    <div className="flex flex-col gap-5">
      {/* `print:hidden` — the controls are not part of the document. */}
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <h1 className="text-xl font-bold text-brand-900">{t('admin.reports.title')}</h1>
      </div>

      <Card
        className="print:hidden"
        title={t('admin.stats.filters')}
        actions={
          <div className="flex items-center gap-2">
            <Button tone="secondary" onClick={() => setApplied(EMPTY_BOUNDS)}>
              {t('admin.inbox.filterReset')}
            </Button>
            <Button onClick={() => setApplied(bounds)}>{t('admin.common.apply')}</Button>
            <Button tone="secondary" onClick={() => window.print()}>
              {t('admin.reports.print')}
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label={t('admin.inbox.filterFrom')}>
            <TextInput
              type="date"
              value={bounds.from}
              onChange={(event) => setBounds({ ...bounds, from: event.target.value })}
            />
          </Field>
          <Field label={t('admin.inbox.filterTo')}>
            <TextInput
              type="date"
              value={bounds.to}
              onChange={(event) => setBounds({ ...bounds, to: event.target.value })}
            />
          </Field>
          <div className="flex items-end pb-2 text-xs text-ink-500">
            {applied.from || applied.to
              ? t('admin.mahallas.window', {
                  from: formatDate(fromDateInput(applied.from)),
                  to: formatDate(fromDateInput(applied.to, true)),
                })
              : t('admin.mahallas.windowAll')}
          </div>
        </div>
      </Card>

      {/* ---- CSV, aggregates only ---------------------------------------- */}
      {allows('analytics.export') ? (
        <Card className="print:hidden" title={t('admin.reports.csvTitle')}>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-ink-700">{t('admin.reports.aggregatesOnly')}</p>
            <div className="flex flex-wrap gap-2">
              {EXPORTS.map((entry) => (
                <a
                  key={entry.name}
                  href={csvHref(entry.name)}
                  className="inline-flex items-center rounded-btn border-1 border-line-300 bg-surface-0 px-4 py-2 text-sm font-semibold text-brand-800 hover:bg-surface-100"
                >
                  {t(entry.labelKey)}
                </a>
              ))}
              {allows('analytics.staff') ? (
                <a
                  href={csvHref('executors')}
                  className="inline-flex items-center rounded-btn border-1 border-line-300 bg-surface-0 px-4 py-2 text-sm font-semibold text-brand-800 hover:bg-surface-100"
                >
                  {t('admin.reports.exportExecutors')}
                </a>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}

      <ErrorBanner message={error} />
      {loading && !breakdown ? <Spinner label={t('admin.common.loading')} /> : null}

      {/* ---- the printable document -------------------------------------- */}
      {breakdown && legal ? (
        <Card title={t('admin.reports.documentTitle')}>
          <div className="flex flex-col gap-6">
            <header className="flex flex-col gap-1">
              <h2 className="text-lg font-bold text-brand-900">
                {t('admin.reports.documentTitle')}
              </h2>
              <p className="text-sm text-ink-500">
                {applied.from || applied.to
                  ? t('admin.mahallas.window', {
                      from: formatDate(fromDateInput(applied.from)),
                      to: formatDate(fromDateInput(applied.to, true)),
                    })
                  : t('admin.mahallas.windowAll')}
              </p>
              {/*
                The SERVER's clock, not the browser's (§14.7). A printed
                government document stamped with a workstation's wrong RTC is a
                document with a wrong date on it.
              */}
              <p className="text-xs text-ink-400">
                {t('admin.reports.generatedAt', {
                  at: formatDateTime(serverNow?.toISOString()),
                })}
              </p>
            </header>

            {kpi ? (
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold uppercase tracking-label text-ink-500">
                  {t('admin.reports.kpiSection')}
                </h3>
                {/*
                  THE LABELS ARE THE DASHBOARD'S OWN (`admin.dashboard.kpi.*`),
                  reused rather than re-coined.

                  A second key family saying "Completion" in slightly different
                  words is how a printed report and the screen it was read off
                  end up disagreeing about what a figure is called — and this is
                  the sheet that leaves the building. One label per number,
                  across both.
                */}
                <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Figure label={t('admin.dashboard.kpi.total')} value={String(kpi.total)} />
                  <Figure
                    label={t('admin.dashboard.kpi.completed')}
                    value={String(kpi.completed)}
                    caption={t('admin.dashboard.kpi.completionPct', {
                      percent: formatPercent(kpi.completion_pct),
                    })}
                  />
                  <Figure
                    label={t('admin.dashboard.kpi.avgResolution')}
                    value={formatMetric(kpi.avg_resolution_days)}
                    caption={t('admin.dashboard.kpi.medianResolution', {
                      days: formatMetric(kpi.median_resolution_days),
                    })}
                  />
                  <Figure label={t('admin.dashboard.kpi.overdue')} value={String(kpi.overdue)} />
                  {/*
                    §14.2.1 — the two SLA numbers stay SEPARATE figures with
                    separate labels, on paper as on screen. The first is
                    compliance with F-6 Art. 28: 15 days from receipt, plus any
                    lawful extension. The second is the hokimlik's own 5-day
                    commitment (owner ruling, 2026-09-28), the date the citizen
                    was given. Two windows, so usually two different
                    percentages, and each caption names its own — a printed
                    report showing one merged figure would put either an
                    invented legal requirement or an overstated promise on a
                    government letterhead.
                  */}
                  <Figure
                    label={t('admin.dashboard.kpi.statutoryOnTime')}
                    value={formatPercent(kpi.statutory_on_time_pct)}
                    caption={t('admin.dashboard.kpi.statutoryHint')}
                  />
                  <Figure
                    label={t('admin.dashboard.kpi.targetOnTime')}
                    value={formatPercent(kpi.target_on_time_pct)}
                    caption={t('admin.dashboard.kpi.targetHint', { days: kpi.target_days })}
                  />
                  <Figure
                    label={t('admin.dashboard.kpi.unassigned')}
                    value={String(kpi.unassigned)}
                  />
                  <Figure
                    label={t('admin.dashboard.kpi.repeatAppellants')}
                    value={String(kpi.repeat_appellants)}
                  />
                </dl>
              </section>
            ) : null}

            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-label text-ink-500">
                {t('admin.stats.legalTypeTitle')}
              </h3>
              {/* Four buckets, unclassified included, on paper as on screen. */}
              <Table
                head={
                  <tr>
                    <Th>{t('admin.column.legalType')}</Th>
                    <Th>{t('admin.column.total')}</Th>
                  </tr>
                }
              >
                {LEGAL_TYPE_BUCKETS.map((key) => (
                  <Tr key={key}>
                    <Td>{t(`admin.legalType.${key}`)}</Td>
                    <Td className="tabular-nums">
                      {(legal as unknown as Record<string, number>)[key] ?? 0}
                    </Td>
                  </Tr>
                ))}
              </Table>
            </section>

            {mahallas ? (
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold uppercase tracking-label text-ink-500">
                  {t('admin.mahallas.title')}
                </h3>
                <Table
                  head={
                    <tr>
                      <Th>{t('admin.column.mahalla')}</Th>
                      <Th>{t('admin.column.total')}</Th>
                      <Th>{t('admin.column.overdue')}</Th>
                      <Th>{t('admin.column.avgDays')}</Th>
                      <Th>{t('admin.column.share')}</Th>
                    </tr>
                  }
                >
                  {mahallas.items.map((item) => (
                    <Tr key={item.mahalla_id}>
                      <Td>{item.mahalla_name}</Td>
                      <Td className="tabular-nums">{item.total}</Td>
                      <Td className="tabular-nums">{item.overdue}</Td>
                      <Td className="tabular-nums">
                        {formatMetric(item.avg_resolution_days)}
                      </Td>
                      <Td className="tabular-nums">{formatPercent(item.share_pct)}</Td>
                    </Tr>
                  ))}
                </Table>
              </section>
            ) : null}
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function Figure({
  label,
  value,
  caption,
}: {
  label: string;
  value: string;
  caption?: string;
}): React.JSX.Element {
  return (
    <div className="rounded-chip bg-surface-50 px-3 py-2">
      <dt className="text-xs uppercase tracking-label text-ink-500">{label}</dt>
      <dd className="text-lg font-bold tabular-nums text-ink-900">{value}</dd>
      {/* The caption is where the 15-day statutory figure gets its "this is the
          law" sentence and the 5-day target gets its "this is our goal" one. */}
      {caption ? <dd className="text-xs text-ink-500">{caption}</dd> : null}
    </div>
  );
}
